import {
    Add as AddIcon,
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    ContentCopy as ContentCopyIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Error as ErrorIcon,
    MoreVert as MoreVertIcon,
    Refresh as RefreshIcon,
    UploadFile as UploadFileIcon,
    CloudUpload as CloudUploadIcon,
    Api as ApiIcon,
    SyncAlt as SyncAltIcon,
} from '@mui/icons-material';
import {
    Alert,
    alpha,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { defaultLocale } from '../../constants/locale';
import { formatDistanceToNow } from 'date-fns';
import { useFormatter } from '../../context/FormatterContext';
import { useNotification } from '../../context/NotificationContext';
import { useTranslation } from '../../hooks/useTranslation';
import { Platform } from '../../services/integration.types';
import { Integration } from '../../services/mocks/data/integrations';
import {
    useUserIntegrationsService,
} from '../../services/user-integrations.service';
import { useUserService } from '../../services/user.service';
import { QUERY_KEYS } from '../../utils/queryKeys';
import ResponsiveTooltip from '../ResponsiveTooltip';
import DeleteIntegrationConfirmModal from './DeleteIntegrationConfirmModal';
import IntegrationExchangeCsvFormComponent from './integration-forms/ExchangeCsv';

interface IntegrationsOverviewProps {
    integrations: Integration[] | undefined;
    onSync: (integration: Integration) => void;
    onDelete: (integration: Integration) => void;
    deletingIntegrationName: string | null;
    isLoading: boolean;
}

const StatusChip: React.FC<{
    integration: Integration;
    onClick?: () => void;
}> = ({ integration, onClick }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    
    // Show badge for processing states or errors
    const showBadge = integration.status.status === 'in_progress' || 
                     integration.status.status === 'error';
    
    if (showBadge) {
        if (integration.status.status === 'in_progress') {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <RefreshIcon
                        fontSize="small"
                        sx={{
                            color: theme.palette.info.main,
                            animation: 'spin 2s linear infinite',
                            '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' },
                            },
                        }}
                    />
                </Box>
            );
        }
        
        if (integration.status.status === 'error') {
            const isClickable = onClick && integration.importTypes?.includes('api');
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <ResponsiveTooltip title={integration.status.message || ''} arrow placement="top">
                        <ErrorIcon
                            fontSize="small"
                            onClick={(e) => {
                                if (isClickable) {
                                    e.stopPropagation();
                                    onClick?.();
                                }
                            }}
                            sx={{
                                color: theme.palette.error.main,
                                cursor: isClickable ? 'pointer' : 'default',
                                '&:hover': isClickable
                                    ? {
                                          opacity: 0.8,
                                      }
                                    : {},
                            }}
                        />
                    </ResponsiveTooltip>
                </Box>
            );
        }
    }
    
    // When completed/idle, just show timestamp
    if (integration.endTime) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {formatDistanceToNow(new Date(integration.endTime * 1000), {
                        addSuffix: true,
                        locale: defaultLocale,
                    })}
                </Typography>
            </Box>
        );
    }
    
    // No data available
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                -
            </Typography>
        </Box>
    );
};

const IntegrationsOverview: React.FC<IntegrationsOverviewProps> = ({
    integrations,
    onSync,
    onDelete,
    deletingIntegrationName,
    isLoading,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const isDarkMode = theme.palette.mode === 'dark';
    const { showNotification } = useNotification();
    const { formatCurrency } = useFormatter();
    const userIntegrationsService = useUserIntegrationsService();
    const queryClient = useQueryClient();

    // State for edit mode
    const [isEditMode, setIsEditMode] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuIntegration, setMenuIntegration] = useState<Integration | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [integrationToDelete, setIntegrationToDelete] = useState<Integration | null>(null);
    const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
    const [csvUploadPlatform, setCsvUploadPlatform] = useState<Platform | null>(null);

    // Use React Query for data fetching
    const queryFn = async () => {
        try {
            const platforms = await userIntegrationsService.getUserIntegrations();
            console.log('Fetched user integrations with React Query:', platforms);
            return platforms;
        } catch (error) {
            console.error('Error fetching user integrations:', error);
            return [];
        }
    };

    const {
        data: userPlatforms = [],
        isLoading: isLoadingUserIntegrations,
        refetch: refetchUserIntegrations,
    } = useQuery({
        queryKey: ['userIntegrations'],
        queryFn,
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 60000, // Cache for 1 minute
    });

    const userService = useUserService();
    
    // Check notifications to determine if banner should be shown
    const { data: notifications, isLoading: isLoadingNotifications } = useQuery({
        queryKey: [QUERY_KEYS.USER_NOTIFICATIONS],
        queryFn: () => userService.getNotifications(20),
        refetchOnWindowFocus: false,
    });

    // Check for selected integrations that are already integrated and update their status
    useEffect(() => {
        if (integrations && userPlatforms) {
            // Find platforms with 'selected' status that are already integrated
            const selectedPlatforms = userPlatforms.filter((platform) => platform.status === 'selected');

            if (selectedPlatforms.length > 0) {
                // Check each selected platform against the integrations list
                selectedPlatforms.forEach((platform) => {
                    // Check if this platform is already integrated
                    const isAlreadyIntegrated = integrations.some(
                        (integration) =>
                            integration.platformType == platform.platform_type &&
                            integration.platformName == platform.name
                    );

                    if (isAlreadyIntegrated) {
                        // Update the status to 'integrated'
                        console.log(`Platform ${platform.name} is already integrated, updating status...`);
                        userIntegrationsService
                            .updateIntegrationStatus(platform.name, platform.platform_type)
                            .then(() => {
                                console.log(`Updated integration status for ${platform.name} to 'integrated'`);
                                // Invalidate the integration setup state query to re-enable navigation
                                queryClient.invalidateQueries({ queryKey: ['integrationSetupState'] });
                            })
                            .catch((error) => {
                                console.error(`Error updating integration status for ${platform.name}:`, error);
                            });
                    }
                });
            }
        }
    }, [integrations, userPlatforms]);

    // Filter platforms that have not been removed
    const userPlatformsFiltered = userPlatforms.filter(
        (platform) =>
            platform.status !== 'removed' &&
            platform.status !== undefined &&
            !(integrations || []).some(
                (integration) =>
                    integration.platformType == platform.platform_type && integration.platformName == platform.name
            )
    );

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, integration: Integration) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuIntegration(integration);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuExited = () => {
        setMenuIntegration(null);
    };

    const handleSync = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            onSync(menuIntegration);
        }
        handleMenuClose();
    };

    const handleDelete = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            setIntegrationToDelete(menuIntegration);
            setDeleteConfirmOpen(true);
        }
        handleMenuClose();
    };

    const handleViewAssets = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            navigate(`/integrations/${menuIntegration.name}`);
        }
        handleMenuClose();
    };

    const handleEdit = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        handleMenuClose();

        if (menuIntegration) {
            navigate(`/integrations/add/${menuIntegration.platformName}?type=api`);
        }
    };

    const handleImportCSV = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (menuIntegration) {
            navigate(`/integrations/add/${menuIntegration.platformName}?type=csv`);
        }
        handleMenuClose();
    };


    const handleConfirmDelete = () => {
        if (integrationToDelete) {
            onDelete(integrationToDelete);
            setDeleteConfirmOpen(false);
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
        setIntegrationToDelete(null);
    };

    const SkeletonRow = () => (
        <TableRow>
            <TableCell>
                <Skeleton variant="text" width={120} />
            </TableCell>
            <TableCell align="right">
                <Skeleton variant="text" width={80} />
            </TableCell>
            <TableCell align="right">
                <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell align="right">
                <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell align="right">
                <Skeleton variant="text" width={100} />
            </TableCell>
            <TableCell align="right">
                <Skeleton variant="text" width={100} />
            </TableCell>
        </TableRow>
    );

    const welcomeBoxStyles = {
        backgroundColor: isDarkMode ? '#1e3a5f' : '#e3f2fd',
        textPrimary: isDarkMode ? '#ffffff' : '#1565c0',
        textSecondary: isDarkMode ? '#bbdefb' : '#1976d2',
        border: isDarkMode ? '#2196f3' : '#bbdefb',
        iconColor: isDarkMode ? '#90caf9' : '#2196f3',
    };

    return (
        <>
            {/* Header with Guidance Banner and Add Integration Button */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    mb: 3,
                    gap: 2,
                }}
            >
                {/* Left side - Guidance Banner */}
                <Box
                    sx={{
                        flexGrow: 1,
                        mr: { xs: 0, sm: 2 },
                        width: { xs: '100%', sm: 'auto' },
                        maxWidth: { sm: 'calc(100% - 180px)' },
                    }}
                >
                </Box>

                {/* Right side - Add Integration button */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: { xs: 2, sm: 0 },
                        alignSelf: { xs: 'center', sm: 'center' },
                        minWidth: '170px',
                        justifyContent: { xs: 'center', sm: 'flex-end' },
                    }}
                >
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/integrations/add')}
                        disabled={isLoading}
                        sx={{
                            whiteSpace: 'nowrap',
                            minWidth: '170px',
                        }}
                    >
                        {t('integrations.overview.addButton')}
                    </Button>
                </Box>
            </Box>

            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                    px: { xs: 1, sm: 2, md: 4 },
                    py: 1.5,
                    overflowX: 'auto',
                    maxWidth: '100%',
                    '&::-webkit-scrollbar': {
                        height: 6,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: alpha(theme.palette.divider, 0.6),
                        borderRadius: 3,
                    },
                    '& .MuiTableBody-root tr:last-child td': {
                        borderBottom: '0 !important',
                    },
                    '& .MuiTableBody-root tr:last-child th': {
                        borderBottom: '0 !important',
                    },
                }}
            >
                <Table
                    sx={{
                        '& .MuiTableCell-root': {
                            py: 2.5,
                            borderBottom: 'none',
                            whiteSpace: { xs: 'normal', sm: 'nowrap' },
                            px: { xs: 1, sm: 2 },
                        },
                        '& thead .MuiTableCell-root': {
                            borderBottom: '2px solid',
                            borderBottomColor: `${theme.palette.divider}`,
                            borderBottomOpacity: 0.6,
                            py: 2,
                        },
                        '& tbody .MuiTableRow-root:not(:last-child) .MuiTableCell-root': {
                            borderBottom: '2px solid',
                            borderBottomColor: `${theme.palette.divider}`,
                            borderBottomOpacity: 0.6,
                        },
                        '& .MuiTypography-root': {
                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                        },
                        tableLayout: { xs: 'auto', md: 'fixed' },
                        borderCollapse: 'collapse',
                        width: '100%',
                        minWidth: {
                            xs: '650px',
                            sm: '850px',
                            md: '100%',
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.account')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.transactions')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.assets')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.value')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.lastSync')}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {t('integrations.overview.table.actions')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <>
                                <SkeletonRow key="skeleton-1" />
                                <SkeletonRow key="skeleton-2" />
                                <SkeletonRow key="skeleton-3" />
                            </>
                        ) : !isLoading && (!integrations || integrations.length === 0) ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ p: { xs: 2, sm: 3 } }}>
                                    <Typography variant="body1" sx={{ p: 3, color: theme.palette.text.secondary }}>
                                        {t('integrations.overview.noIntegrations')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {integrations.map((integration) => (
                                    <TableRow
                                        key={integration.name + integration.jobId}
                                        sx={{
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover,
                                            },
                                        }}
                                        onClick={() => {
                                            if (!anchorEl) {
                                                navigate(`/integrations/${integration.name}`);
                                            }
                                        }}
                                    >
                                        <TableCell component="th" scope="row">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                }}
                                            >
                                                <Avatar
                                                    src={
                                                        integration.logoUri
                                                            ? 'https://cointracking.info/' + integration.logoUri
                                                            : ''
                                                    }
                                                    alt={integration.name}
                                                    sx={{
                                                        width: 36,
                                                        height: 36,
                                                    }}
                                                />
                                                <Box>
                                                    {integration.walletAddress ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                }}
                                                            >
                                                                {integration.name}
                                                            </Typography>
                                                            <ResponsiveTooltip
                                                                title={integration.walletAddress}
                                                                arrow
                                                                placement="top"
                                                            >
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (integration.walletAddress) {
                                                                            navigator.clipboard.writeText(
                                                                                integration.walletAddress
                                                                            );
                                                                            showNotification(
                                                                                t('common.copied'),
                                                                                'success'
                                                                            );
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        ml: 0.5,
                                                                        p: 0.5,
                                                                        color: theme.palette.text.secondary,
                                                                        '&:hover': {
                                                                            color: theme.palette.primary.main,
                                                                        },
                                                                    }}
                                                                >
                                                                    <ContentCopyIcon
                                                                        fontSize="small"
                                                                        sx={{ fontSize: '0.85rem' }}
                                                                    />
                                                                </IconButton>
                                                            </ResponsiveTooltip>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {integration.name}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {integration.transactionsCount}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {integration.assetsCount}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                {formatCurrency(integration.value)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <StatusChip
                                                integration={integration}
                                                onClick={
                                                    integration.status.status === 'error' && integration.importTypes?.includes('api')
                                                        ? () => {
                                                              // Set the current integration as the one to edit
                                                              setMenuIntegration(integration);
                                                              setEditIntegration(integration);
                                                              setEditImportType('api');
                                                              setIsAddModalOpen(true);
                                                          }
                                                        : undefined
                                                }
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                onClick={(event) => handleMenuClick(event, integration)}
                                                size="small"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    '&:hover': {
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                    },
                                                }}
                                            >
                                                {deletingIntegrationName === integration.name ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <MoreVertIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={Boolean(anchorEl)}
                                                onClose={handleMenuClose}
                                                TransitionProps={{
                                                    onExited: handleMenuExited,
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                                slotProps={{
                                                    paper: {
                                                        sx: {
                                                            width: 200,
                                                            maxWidth: '100%',
                                                            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.2)',
                                                            mt: 0.5,
                                                            '& .MuiList-root': {
                                                                py: 0.5,
                                                            },
                                                        },
                                                    },
                                                }}
                                                MenuListProps={{
                                                    dense: true,
                                                    onClick: (e) => e.stopPropagation(),
                                                }}
                                                sx={{
                                                    '& .MuiMenuItem-root': {
                                                        py: 1,
                                                        px: 2,
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                                        },
                                                    },
                                                    '& .MuiListItemIcon-root': {
                                                        minWidth: 32,
                                                        color: theme.palette.text.secondary,
                                                    },
                                                    '& .MuiListItemText-primary': {
                                                        fontWeight: 600,
                                                        fontSize: '0.875rem',
                                                    },
                                                }}
                                            >
                                                {menuIntegration?.jobId &&
                                                    menuIntegration?.status?.status !== 'error' && (
                                                        <MenuItem
                                                            onClick={handleSync}
                                                            disabled={!!deletingIntegrationName}
                                                        >
                                                            <ListItemIcon>
                                                                <RefreshIcon fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={t('integrations.overview.actions.sync')}
                                                            />
                                                        </MenuItem>
                                                    )}
                                                {menuIntegration?.platformType === 'exchange' &&
                                                    menuIntegration?.jobId && (
                                                        <MenuItem
                                                            onClick={handleEdit}
                                                            disabled={!!deletingIntegrationName}
                                                        >
                                                            <ListItemIcon>
                                                                <EditIcon fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={t('integrations.overview.actions.edit')}
                                                            />
                                                        </MenuItem>
                                                    )}
                                                {menuIntegration?.importTypes &&
                                                    menuIntegration.importTypes.includes('csv') && (
                                                        <MenuItem
                                                            onClick={handleImportCSV}
                                                            disabled={!!deletingIntegrationName}
                                                        >
                                                            <ListItemIcon>
                                                                <UploadFileIcon fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={t('integrations.overview.actions.importCsv')}
                                                            />
                                                        </MenuItem>
                                                    )}
                                                <MenuItem
                                                    onClick={handleViewAssets}
                                                    disabled={!!deletingIntegrationName}
                                                >
                                                    <ListItemIcon>
                                                        <AssessmentIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={t('integrations.overview.actions.viewAssets')}
                                                    />
                                                </MenuItem>
                                                <MenuItem
                                                    onClick={handleDelete}
                                                    disabled={
                                                        !!deletingIntegrationName ||
                                                        menuIntegration?.status?.status === 'in_progress'
                                                    }
                                                    sx={{
                                                        color: theme.palette.error.main,
                                                        '& .MuiListItemIcon-root': {
                                                            color: theme.palette.error.main,
                                                        },
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <DeleteIcon fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={t('integrations.overview.actions.delete')} />
                                                </MenuItem>
                                            </Menu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <DeleteIntegrationConfirmModal
                open={deleteConfirmOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                integration={integrationToDelete}
                isDeleting={!!deletingIntegrationName && integrationToDelete?.name === deletingIntegrationName}
            />

            {/* Direct CSV Upload Modal for Custom Integrations */}
            {showCsvUploadModal && csvUploadPlatform && (
                <Dialog
                    open={showCsvUploadModal}
                    onClose={() => setShowCsvUploadModal(false)}
                    maxWidth="lg"
                    fullWidth
                    BackdropProps={{
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(5px)',
                        },
                    }}
                    PaperProps={{
                        sx: {
                            width: '80%',
                            maxWidth: '1000px',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            bgcolor: (theme) => theme.palette.background.paper,
                            '& .MuiDialogContent-root': {
                                bgcolor: (theme) => theme.palette.background.paper,
                            },
                        },
                    }}
                >
                    <DialogTitle sx={{ bgcolor: (theme) => theme.palette.background.paper }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                                {csvUploadPlatform.display_name}
                            </Typography>
                            <IconButton
                                aria-label="close"
                                onClick={() => setShowCsvUploadModal(false)}
                                sx={{ color: theme.palette.text.secondary }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <IntegrationExchangeCsvFormComponent
                                platform={csvUploadPlatform}
                                onJobCreated={() => {
                                    setShowCsvUploadModal(false);
                                    if (onSync && editIntegration) onSync(editIntegration);
                                }}
                                importer={{
                                    id: 0,
                                    customText: '',
                                    displayName: 'File Upload',
                                    position: 0,
                                    type: 'excel',
                                    uri: '/import/import_xls',
                                    uploadFile: '',
                                    importFolder: '',
                                    customFields: [],
                                }}
                            />
                        </Box>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
};

export default IntegrationsOverview;
